<script setup lang="ts">
import { onMounted } from 'vue'
import { APP_URL, SERVICE_NAME, OLD_CORPORATE_SITE_URL, CORPORATE_SITE_URL, HELP_SITE_URL } from '@/constants/global'
import { IBreadcrumb } from '@/types/common/footer'
import { IPrefApiResponse } from '@/types/common/pref'
import { ICategoriesApiDetail } from '@/types/common/categories'
import { UserRole } from '@/types/common/users'
import { getGroupedPrefs } from '@/utils/options/prefectures'

onMounted(() => {
  // 既にある場合は読み込まない(router遷移時に同じ処理は行わない)
  if (document.getElementById('gcs-engine')?.hasChildNodes()) return
  const cx = '011736250731746215006:2j-m5tj4rmq'
  const gcse = document.createElement('script')
  gcse.type = 'text/javascript'
  gcse.async = true
  gcse.src = 'https://cse.google.com/cse.js?cx=' + cx
  document.body.appendChild(gcse)
  gcse.onload = () => {
    const searchBox = document.createElement('gcse:searchbox-only')
    searchBox.setAttribute('enablehistory', 'true')
    searchBox.setAttribute('resultsurl', APP_URL + '/google-search')
    document.getElementById('gcs-engine')?.appendChild(searchBox)
  }
})

const props = defineProps<{
  breadcrumbs: IBreadcrumb[]
  pref: IPrefApiResponse['pref']
  categories: ICategoriesApiDetail[]
  userRole: UserRole
}>()

const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}
</script>

<template>
  <footer id="origin-footer" class="footer">
    <div class="op-color-box op-bg-light-cream origin-breadcrumb">
      <div class="op-row op-space-inside-small">
        <div class="op-col op-col-9-md op-col-6-sm op-col-6-xs op-text-left">
          <ul>
            <li class="home">
              <a href="/"><i></i>ホーム</a>
            </li>
            <li v-for="(breadcrumb, key) in props.breadcrumbs" :key="key">
              <a v-if="breadcrumb.path" :href="breadcrumb.path">{{ breadcrumb.label }}</a>
              <span v-else>{{ breadcrumb.label }}</span>
            </li>
          </ul>
        </div>
        <div class="op-col op-col-3 op-col-6-sm op-col-6-xs op-text-right origin-link-to-top">
          <p class="pagetop">
            <span @click="scrollToTop"
              >ページトップへ<i class="op-icon op-icon-arrow op-text-large op-margin-left-10"></i>
            </span>
          </p>
        </div>
      </div>
    </div>
    <div class="op-text-center op-text-dark-brown op-padding-top-20 op-padding-bottom-20 origin-search">
      <div class="op-row op-row-center">
        <div class="op-col op-col-7-md op-col-10-sm op-col-10-xs">
          <div class="op-text-center op-text-dark-brown op-padding-top-20 op-padding-bottom-20 origin-search">
            <div id="gcs-engine"></div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="op-color-box op-padding-x-20-md op-padding-y-60-md origin-footer-nav op-bg-dark-brown-md op-bg-light-cream-sm op-bg-light-cream-xs"
    >
      <div class="op-row op-text-left">
        <div class="op-col op-col-4-md op-col-12-sm op-col-12-xs">
          <nav class="op-padding-top-100-md op-padding-left-20-md origin-nav">
            <ul class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/policy" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>利用規約
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a
                  :href="`${CORPORATE_SITE_URL}/about`"
                  class="op-padding-left-10-sm op-padding-left-10-xs"
                  target="_blank"
                >
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>企業情報
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a :href="`${OLD_CORPORATE_SITE_URL}/privacy`" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>プライバシーポリシー
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a
                  :href="`${CORPORATE_SITE_URL}/cookie`"
                  class="op-padding-left-10-sm op-padding-left-10-xs"
                >
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>クッキーポリシー
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/cancel_policy" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>キャンセルポリシー
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/customer_harassment" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>カスタマーハラスメントに対する基本指針
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a :href="`${CORPORATE_SITE_URL}/security_policy`" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>情報セキュリティ基本方針
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/tradelaw" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>特定商取引法に基づく表示
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/insurance" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>保険制度
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a
                  :href="HELP_SITE_URL"
                  target="_blank"
                  rel="noopener"
                  class="op-padding-left-10-sm op-padding-left-10-xs"
                >
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>よくあるご質問・お問い合わせ
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/note" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>{{ SERVICE_NAME }}が発信する情報マガジン
                  {{ SERVICE_NAME }}写真部
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <div class="op-col op-col-2-md op-col-12-sm op-col-12-xs">
          <p
            class="op-text-size16 op-border-bottom op-border-dark-cream op-border-size2 op-padding-bottom-5 op-display-none-sm op-display-none-xs"
          >
            <a href="/category">撮影ジャンル</a>
          </p>
          <nav class="op-padding-left-20-md origin-nav">
            <ul class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white op-display-none-md">
                <a href="/category" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>撮影ジャンル
                </a>
              </li>
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white op-display-none-md">
                <a href="/area" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>出張エリア
                </a>
              </li>
              <li v-for="(category, key) in props.categories" :key="key" class="op-display-none-sm op-display-none-xs">
                <a :href="'/category/' + category.slug">{{ category.name }}</a>
              </li>
            </ul>
          </nav>
        </div>
        <div class="op-col op-col-3-md op-col-12-sm op-col-12-xs op-padding-left-20-md">
          <p
            class="op-text-size16 op-border-bottom op-border-dark-cream op-border-size2 op-padding-bottom-5 op-display-none-sm op-display-none-xs"
          >
            <a href="/area">出張エリア</a>
          </p>
          <div v-for="(prefs, region, key) in getGroupedPrefs(props.pref)" :key="key">
            <p class="op-text-dark-cream op-text-small op-margin-bottom-none op-display-none-sm op-display-none-xs">
              {{ region }}
            </p>
            <p class="pref op-margin-bottom-none op-display-none-sm op-display-none-xs">
              <a v-for="prefValue in prefs" :key="prefValue.id" :href="'/area/' + prefValue.id">{{ prefValue.pref }}</a>
            </p>
          </div>
        </div>
        <div class="op-col op-col-3-md op-col-12-sm op-col-12-xs op-padding-left-20-md">
          <p
            class="op-text-size16 op-text-light-blue op-border-bottom op-border-light-blue op-border-size2 op-padding-bottom-5 op-display-none-sm op-display-none-xs"
          >
            写真をとってほしい方
          </p>
          <nav class="op-padding-left-20-md op-margin-bottom-20-md origin-nav">
            <ul class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/photographers" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>フォトグラファー一覧
                </a>
              </li>
            </ul>
            <p
              class="op-text-white-md op-text-dark-brown-sm op-text-dark-brown-xs op-border-bottom-sm op-border-bottom-xs op-border-white op-margin-bottom-none op-padding-left-10-sm op-padding-left-10-xs origin-caption"
            >
              撮影の事例を見る
            </p>
            <nav class="op-padding-left-20-md origin-nav">
              <ul class="op-margin-bottom-none origin-links">
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/photogallery" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>フォトギャラリー一覧
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/topics" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>出張撮影の特集
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/photograph_calendar" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>年間撮影カレンダー
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/photograph_report" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>撮影レポート
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/award" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>人気フォトグラファーランキング
                  </a>
                </li>
              </ul>
            </nav>
            <p
              class="op-text-white-md op-text-dark-brown-sm op-text-dark-brown-xs op-border-bottom-sm op-border-bottom-xs op-border-white op-margin-bottom-none op-padding-left-10-sm op-padding-left-10-xs origin-caption"
            >
              {{ SERVICE_NAME }}について知る
            </p>

            <nav class="op-padding-left-20-md origin-nav">
              <ul class="op-margin-bottom-none origin-links">
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/price/reason" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>{{ SERVICE_NAME }}の価格のヒミツ
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/beginner" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>初めての方へ
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/guide" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>ご利用の流れ
                  </a>
                </li>
                <li class="op-display-none-sm op-display-none-xs">
                  <a
                    href="https://help.our-photo.co/hc/ja/categories/900000047746-ご依頼者向け"
                    target="_blank"
                    rel="noopener"
                  >
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>よくあるご質問
                  </a>
                </li>
              </ul>
            </nav>
            <ul v-if="props.userRole === 'guest'" class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/sign_up/user" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>会員登録（無料）
                </a>
              </li>
            </ul>
            <ul class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/offer/how-to-use" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>撮影リクエスト
                </a>
              </li>
            </ul>
            <ul class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/photogoods" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>フォトグッズ
                </a>
              </li>
            </ul>
            <nav class="op-padding-left-20-md origin-nav">
              <ul class="op-margin-bottom-none origin-links">
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/photobook" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>フォトブック
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/canvasprint" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>キャンバスプリント
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/photoprint" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>写真プリント
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/newborn-acrylic-frame" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>ニューボーン アクリルフレーム
                  </a>
                </li>
                <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                  <a href="/photomount" class="op-padding-left-20-sm op-padding-left-20-xs">
                    <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>写真台紙プリント
                  </a>
                </li>
              </ul>
            </nav>
          </nav>
          <p
            class="op-text-size16 op-text-green op-border-bottom op-border-green op-border-size2 op-padding-bottom-5 op-display-none-sm op-display-none-xs"
          >
            写真をとりたい方
          </p>
          <nav class="op-padding-left-20-md origin-nav">
            <ul class="op-margin-bottom-none origin-links">
              <li class="op-border-bottom-sm op-border-bottom-xs op-border-white">
                <a href="/photographer-entry" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>フォトグラファー募集中
                </a>
              </li>
              <li>
                <a href="/sign_up/photographer" class="op-padding-left-10-sm op-padding-left-10-xs">
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>フォトグラファー登録（無料）
                </a>
              </li>
              <li class="op-display-none-sm op-display-none-xs">
                <a
                  href="https://help.our-photo.co/hc/ja/categories/900000043883-フォトグラファー向け"
                  target="_blank"
                  rel="noopener"
                >
                  <i class="op-icon op-icon-arrow op-margin-right-5-md"></i>よくあるご質問
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <div
        class="op-text-left-md op-text-center-sm op-text-center-xs op-color-box op-bg-dark-brown-sm op-bg-dark-brown-xs op-padding-top-20-sm op-padding-top-20-xs op-padding-bottom-20-sm op-padding-bottom-20-xs origin-icon-set"
      >
        <a href="/" class="op-margin-bottom-10 origin-logo">{{ SERVICE_NAME }}</a>
        <nav class="origin-sns-icons">
          <a
            href="https://www.facebook.com/ourphoto.co"
            class="op-margin-right-20-md op-margin-right-10-sm op-margin-right-10-xs origin-icon origin-icon-facebook"
            target="_blank"
            >FB</a
          >
          <a
            href="https://twitter.com/ourphotojapan"
            class="op-margin-right-20-md op-margin-right-10-sm op-margin-right-10-xs origin-icon origin-icon-twitter"
            target="_blank"
            >TW</a
          >
          <a
            href="https://www.instagram.com/ourphoto_official/"
            class="op-margin-right-20-md op-margin-right-10-sm op-margin-right-10-xs origin-icon origin-icon-instagram"
            target="_blank"
            >In</a
          >
          <a href="https://line.me/R/ti/p/%40fpf9811w" class="origin-icon origin-icon-line" target="_blank">LI</a>
        </nav>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.footer {
  .pref {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }
}
</style>
